import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, FileText, Clock, Upload, MessageSquare, Calendar, DollarSign, Download, CheckCircle, Loader2, Users } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';
import DocumentUploadSection from '@/components/app/DocumentUploadSection';
import { getStatusStyle } from '@/lib/orderStatusStyles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [company, setCompany] = useState(null);
  const [product, setProduct] = useState(null);
  const [completedDocs, setCompletedDocs] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [members, setMembers] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadingSubmission, setDownloadingSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOrderData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [orderRes, docsRes, submissionsRes, activityRes] = await Promise.all([
        axios.get(`${API}/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/orders/${orderId}/completed-documents`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { documents: [] } })),
        axios.get(`${API}/orders/${orderId}/submissions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { submissions: [] } })),
        axios.get(`${API}/orders/${orderId}/activity`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { activity: [] } }))
      ]);
      
      setOrder(orderRes.data);
      setCompletedDocs(docsRes.data.documents || []);
      setSubmissions(submissionsRes.data.submissions || []);
      setActivityLog(activityRes.data.activity || []);
      
      // Fetch company and product details in parallel
      const promises = [];
      
      if (orderRes.data.company_id) {
        promises.push(
          axios.get(`${API}/companies/${orderRes.data.company_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => setCompany(res.data)).catch(() => {})
        );
        promises.push(
          axios.get(`${API}/companies/${orderRes.data.company_id}/members`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => setMembers(res.data.members || [])).catch(() => setMembers([]))
        );
      }
      
      if (orderRes.data.product_id) {
        promises.push(
          axios.get(`${API}/products/${orderRes.data.product_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => setProduct(res.data)).catch(() => {})
        );
      }
      
      await Promise.all(promises);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to load order details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (docId, fileName) => {
    setDownloadingDoc(docId);
    try {
      const res = await axios.get(`${API}/download/presigned/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open download URL in new tab
      window.open(res.data.download_url, '_blank');
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloadingDoc(null);
    }
  };

  const handleSubmissionDownload = async (submissionId, fileName) => {
    setDownloadingSubmission(submissionId);
    try {
      const res = await axios.get(`${API}/submissions/${submissionId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open download URL in new tab
      window.open(res.data.download_url, '_blank');
    } catch (err) {
      console.error('Submission download error:', err);
    } finally {
      setDownloadingSubmission(null);
    }
  };

  useEffect(() => {
    fetchOrderData();
  }, [orderId, token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const formatAmount = (amount) => {
    if (typeof amount !== 'number') return '-';
    return `$${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-400">Loading order details...</div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400">{error}</p>
          <Link to="/dashboard/orders" className="mt-2 text-sm text-red-400 hover:text-red-300 underline">
            Back to orders
          </Link>
        </div>
      </AppLayout>
    );
  }

  if (!order) {
    return (
      <AppLayout>
        <div className="text-center py-20">
          <p className="text-gray-400">Order not found</p>
          <Link to="/dashboard/orders" className="mt-2 text-sm text-[#059669] hover:underline">
            Back to orders
          </Link>
        </div>
      </AppLayout>
    );
  }

  const statusStyle = getStatusStyle(order.status);

  // Payment is "paid" if the order has the explicit field, OR has moved past
  // payment in lifecycle (defensive fallback for orders predating the field).
  const isPaid =
    order.payment_status === 'paid' ||
    ['documents_pending', 'under_review', 'signature_pending',
     'signature_review', 'approved', 'completed'].includes(order.status);

  // Banner state for documents lifecycle.
  //   - Action banner: status is documents_pending AND nothing submitted yet
  //   - Under-review banner: status is documents_pending AND at least one submission exists
  const showDocumentReminder =
    order.status === 'documents_pending' && submissions.length === 0;
  const showDocsSubmittedBanner =
    order.status === 'documents_pending' && submissions.length > 0;

  // Member-details prompt for formation orders only.
  const showMemberReminder =
    ['llc_formation', 'c_corp_formation'].includes(order.service_type) &&
    order.status === 'documents_pending' &&
    members.length === 0;
  
  // Scroll to document upload section
  const scrollToDocuments = () => {
    const docSection = document.querySelector('[data-testid="document-upload-section"]');
    if (docSection) {
      docSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <AppLayout>
      {/* Member Details Required banner (formation orders only) */}
      {showMemberReminder && (
        <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4" data-testid="member-reminder-banner">
          <div className="flex items-start gap-3">
            <span className="text-2xl">👥</span>
            <div className="flex-1">
              <p className="text-amber-400 font-medium mb-1">Member Details Required</p>
              <p className="text-amber-300/80 text-sm mb-3">
                Please add your company members so we can start your formation.
              </p>
              <button
                onClick={() => navigate(`/dashboard/companies/${order.company_id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded hover:bg-amber-400 transition-colors text-sm"
                data-testid="add-members-btn"
              >
                <Users className="w-4 h-4" />
                Add Members Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Reminder Banner */}
      {showDocumentReminder && (
        <div className="mb-6 bg-[#059669]/10 border border-[#059669]/30 rounded-lg p-4" data-testid="doc-reminder-banner">
          <div className="flex items-start gap-3">
            <span className="text-2xl">📄</span>
            <div className="flex-1">
              <p className="text-[#059669] font-medium mb-1">Action Required</p>
              <p className="text-[#059669]/80 text-sm mb-3">
                Please upload the required documents to proceed with your order.
              </p>
              <button 
                onClick={scrollToDocuments}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#059669] text-black font-medium rounded hover:bg-[#00D46E] transition-colors text-sm"
                data-testid="upload-docs-btn"
              >
                <Upload className="w-4 h-4" />
                Upload Documents Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Documents Submitted — Under Review banner */}
      {showDocsSubmittedBanner && (
        <div
          className="mb-6 bg-teal-500/10 border border-teal-500/30 rounded-lg p-4"
          data-testid="docs-submitted-banner"
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl">✅</span>
            <div className="flex-1">
              <p className="text-teal-300 font-medium mb-1">Documents Submitted</p>
              <p className="text-teal-200/80 text-sm">
                Your documents are under review. We&apos;ll notify you of any updates.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Back Link */}
      <Link 
        to="/dashboard/orders" 
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        data-testid="back-to-orders"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Orders
      </Link>

      {/* Page Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">{order.product_name || 'Order'}</h1>
          <p className="text-gray-400 text-sm mt-1">Order #{order.id?.slice(0, 8)}</p>
        </div>
        <span className={`px-4 py-2 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text}`}>
          {statusStyle.label}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Order Info */}
          <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="order-info">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-[#059669]" />
              Order Details
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Service</p>
                <p className="text-white">{order.product_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Company</p>
                <p className="text-white">{company?.company_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">State</p>
                <p className="text-white">{company?.state_of_formation || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Order Date</p>
                <p className="text-white">{formatDate(order.created_at)}</p>
              </div>
            </div>
          </section>

          {/* Order Information - required fields (from product) + client-submitted values */}
          {((product?.information_fields?.length > 0) || (order.collected_information && Object.keys(order.collected_information).length > 0)) && (
            <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="order-information">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-[#059669]" />
                Order Information
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {product?.information_fields?.length > 0 ? (
                  // Display every product-configured field; show a placeholder when not yet provided
                  product.information_fields.map((field) => {
                    const value = order.collected_information?.[field.field_key];
                    const hasValue = !(value === undefined || value === null || value === '');
                    return (
                      <div key={field.field_key}>
                        <p className="text-sm text-gray-500">{field.label}{field.required ? ' *' : ''}</p>
                        <p className={hasValue ? 'text-white' : 'text-gray-500 italic'}>{hasValue ? value : 'Not provided yet'}</p>
                      </div>
                    );
                  })
                ) : (
                  // Fallback: Display raw key-value pairs with formatted keys
                  Object.entries(order.collected_information).map(([key, value]) => {
                    if (value === undefined || value === null || value === '') return null;
                    // Format key: snake_case to Title Case
                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                    return (
                      <div key={key}>
                        <p className="text-sm text-gray-500">{label}</p>
                        <p className="text-white">{value}</p>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          )}

          {/* Payment Info */}
          <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="payment-info">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-[#059669]" />
              Payment
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Amount</p>
                <p className="text-white text-xl font-semibold">{formatAmount(order.final_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="text-white capitalize">{order.payment_method || 'Card'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Status</p>
                <p className={isPaid ? 'text-[#059669]' : 'text-yellow-400'}>
                  {isPaid ? 'Paid' : 'Pending'}
                </p>
              </div>
            </div>
          </section>

          {/* Submitted Documents - Documents uploaded by client during order */}
          {submissions.length > 0 && (
            <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="submitted-documents">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <Upload className="w-5 h-5 text-[#059669]" />
                Your Submitted Documents
              </h2>
              
              <div className="space-y-3">
                {submissions.map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between p-4 bg-[#0b1310] border border-gray-700 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <FileText className="w-6 h-6 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium">{sub.document_type}</p>
                        <p className="text-gray-500 text-sm truncate">{sub.file_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            sub.status === 'approved' 
                              ? 'bg-green-500/20 text-green-400'
                              : sub.status === 'rejected'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-gray-600/20 text-gray-400'
                          }`}>
                            {sub.status === 'approved' ? 'Approved' : sub.status === 'rejected' ? 'Rejected' : 'Under Review'}
                          </span>
                          {sub.source === 'vault' && (
                            <span className="text-xs text-gray-500">From vault</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSubmissionDownload(sub.id, sub.file_name)}
                      disabled={downloadingSubmission === sub.id}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 disabled:opacity-50 transition-colors ml-4"
                      data-testid={`download-submission-${sub.id}`}
                    >
                      {downloadingSubmission === sub.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Documents from StartEase - Show when documents exist */}
          {completedDocs.length > 0 && (
            <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="completed-documents">
              {/* Success Banner */}
              <div className="bg-[#059669]/20 border border-[#059669]/30 rounded-lg p-4 mb-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-[#059669] flex-shrink-0" />
                <div>
                  <p className="text-[#059669] font-semibold">Your documents are ready!</p>
                  <p className="text-[#059669]/80 text-sm">Download your documents below.</p>
                </div>
              </div>
              
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-[#059669]" />
                Documents from StartEase
              </h2>
              
              <div className="space-y-3">
                {completedDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 bg-[#0b1310] border border-gray-700 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-6 h-6 text-[#059669] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white font-medium">{doc.document_type}</p>
                        <p className="text-gray-500 text-sm truncate">{doc.file_name}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(doc.id, doc.file_name)}
                      disabled={downloadingDoc === doc.id}
                      className="flex items-center gap-2 px-4 py-2 bg-[#059669] text-black rounded-lg font-medium hover:bg-[#00D46E] disabled:opacity-50 transition-colors"
                    >
                      {downloadingDoc === doc.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Activity Log */}
          <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="activity-log">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-[#059669]" />
              Activity Log
            </h2>
            
            {activityLog.length > 0 ? (
              <div className="space-y-4">
                {activityLog.map((activity, index) => (
                  <div key={activity.id || index} className="flex gap-4 relative">
                    {/* Timeline line */}
                    {index < activityLog.length - 1 && (
                      <div className="absolute left-[11px] top-6 w-0.5 h-full bg-gray-800" />
                    )}
                    
                    {/* Timeline dot */}
                    <div className="w-6 h-6 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0 z-10">
                      <div className="w-2 h-2 bg-[#059669] rounded-full" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pb-4">
                      <p className="text-white text-sm">{activity.message}</p>
                      <p className="text-gray-500 text-xs">{formatDate(activity.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500 text-sm">Order created</p>
                <p className="text-gray-600 text-xs">{formatDate(order.created_at)}</p>
              </div>
            )}
          </section>

          {/* Admin Notes */}
          {order.admin_notes && order.admin_notes.length > 0 && (
            <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="admin-notes">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <MessageSquare className="w-5 h-5 text-[#059669]" />
                Notes from Admin
              </h2>
              
              <div className="space-y-4">
                {order.admin_notes.map((note, index) => (
                  <div key={index} className="bg-[#0b1310] rounded-lg p-4">
                    <p className="text-white text-sm">{note.content}</p>
                    <p className="text-gray-500 text-xs mt-2">{formatDate(note.timestamp)}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Document Upload Section */}
          {(product?.service_type || order.service_type) && (
            <DocumentUploadSection
              orderId={orderId}
              orderStatus={order.status}
              serviceType={product?.service_type || order.service_type}
              token={token}
              documentRequirements={product?.document_requirements || []}
              onDocumentsChange={fetchOrderData}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <section className="bg-[#111827] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Quick Info</h2>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">Created:</span>
                <span className="text-white">{formatDate(order.created_at)?.split(',')[0]}</span>
              </div>
              
              {order.updated_at && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-400">Updated:</span>
                  <span className="text-white">{formatDate(order.updated_at)?.split(',')[0]}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">Company:</span>
                <Link to={`/dashboard/companies/${company?.id}`} className="text-[#059669] hover:underline">
                  {company?.company_name || '-'}
                </Link>
              </div>
              
              {product?.service_type && (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-400">Service:</span>
                  <span className="text-white capitalize">{product.service_type.replace(/_/g, ' ')}</span>
                </div>
              )}
            </div>
          </section>

          {/* View All Documents Link */}
          <Link
            to="/dashboard/documents"
            className="block w-full text-center px-4 py-3 rounded-lg font-medium text-[#059669] border border-[#059669]/30 hover:bg-[#059669]/10 transition-colors"
            data-testid="view-all-documents-btn"
          >
            View All Documents
          </Link>
        </div>
      </div>
    </AppLayout>
  );
};

export default OrderDetailPage;
